/**
 * API client for the admin console.
 *
 * Access tokens live 15 minutes, so a 401 mid-session is normal rather than
 * exceptional. `request` refreshes once and replays the call, and concurrent
 * 401s share a single refresh so eight parallel table loads do not fire eight
 * refreshes and invalidate each other through token rotation.
 */

const BASE = '/api/v1';
/**
 * Which site this bundle is: the admin console, or the Master Admin site
 * (built and served with `--mode master`). The two are separate origins, so
 * they could not share a session if they tried; the keys differ as well so
 * that stays true if somebody ever serves both from one host.
 */
export const IS_MASTER = import.meta.env.MODE === 'master';
const ACCESS_KEY = IS_MASTER ? 'isb.master.access' : 'isb.admin.access';
const REFRESH_KEY = IS_MASTER ? 'isb.master.refresh' : 'isb.admin.refresh';

export interface ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
}

export interface Page<T> {
  items: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    [extra: string]: unknown;
  };
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshing: Promise<boolean> | null = null;

async function runRefresh(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;

  const res = await fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) {
    tokens.clear();
    return false;
  }
  const body = (await res.json()) as { accessToken: string; refreshToken: string };
  tokens.set(body.accessToken, body.refreshToken);
  return true;
}

function refreshOnce(): Promise<boolean> {
  // Shared, so parallel 401s do not each rotate the refresh token — which
  // would make all but one of them invalid and log the user out.
  refreshing ??= runRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function toError(res: Response): Promise<ApiError> {
  let body: Record<string, unknown> = {};
  try {
    body = (await res.json()) as Record<string, unknown>;
  } catch {
    /* a non-JSON error body is still an error */
  }
  const error = new Error(
    (body.message as string) || `Request failed (${res.status})`,
  ) as ApiError;
  error.status = res.status;
  error.code = body.code as string | undefined;
  error.details = body.details;
  return error;
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers = new Headers(init.headers);
  // A file upload sets its own multipart boundary; anything else is JSON.
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const access = tokens.access;
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401 && retry) {
    if (await refreshOnce()) return request<T>(path, init, false);
    tokens.clear();
    window.dispatchEvent(new CustomEvent('isb:signed-out'));
    throw await toError(res);
  }

  if (!res.ok) throw await toError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Drops empty values so a cleared filter does not become `?status=`. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** One file, sent as multipart field `file`. */
  upload: <T>(path: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<T>(path, { method: 'POST', body: form });
  },
};

export async function adminLogin(email: string, password: string) {
  // The master site has its own sign-in, which answers only for a super admin.
  const res = await fetch(`${BASE}/auth/${IS_MASTER ? 'master' : 'admin'}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw await toError(res);
  const body = (await res.json()) as {
    accessToken: string;
    refreshToken: string;
    user: { id: string; displayName: string | null; mobile: string; roles: string[] };
  };
  tokens.set(body.accessToken, body.refreshToken);
  return body.user;
}
